// AGC: NewRevenue — Create new revenue form
// AGC: Has title, value, category, date, and optional notes fields
// AGC: Revenues are money IN — grain sales, machinery sales, asset sales, subsidies, rental, etc.
// AGC: Associated with the authenticated user account

import React, { useState } from 'react'
import { TopBar, GlassCard, Button } from '../shared'
import type { Screen, RevenueCategory } from '../../types'
import { useTranslation } from '../../hooks/useTranslation'
import { useFinancial } from '../../context'
import toast from 'react-hot-toast'

interface NewRevenueProps {
  onNavigate: (screen: Screen) => void
  onBack: () => void
}

interface RevenueFormData {
  title: string
  value: string // AGC: String to handle input, converted to number on submit
  category: RevenueCategory
  notes: string
}

// AGC: Available revenue categories — includes all sale types (grains, machinery, tools, agroquimics, stock, animals, assets, etc.)
const REVENUE_CATEGORIES: { value: RevenueCategory; labelKey: string }[] = [
  { value: 'grain_sale', labelKey: 'grainSale' },
  { value: 'machinery_sale', labelKey: 'machinerySale' },
  { value: 'tool_sale', labelKey: 'toolSale' },
  { value: 'agrochemical_sale', labelKey: 'agrochemicalSale' },
  { value: 'stock_sale', labelKey: 'stockSale' },
  { value: 'animal_sale', labelKey: 'animalSale' },
  { value: 'asset_sale', labelKey: 'assetSale' },
  { value: 'service', labelKey: 'service' },
  { value: 'subsidy', labelKey: 'subsidy' },
  { value: 'rental', labelKey: 'rental' },
  { value: 'other', labelKey: 'other' },
]

/**
 * AGC: NewRevenue — Form to create a new revenue entry
 * AGC: Title, value, category, date (auto), and optional notes
 */
export const NewRevenue: React.FC<NewRevenueProps> = ({ onNavigate, onBack }) => {
  const { t } = useTranslation()
  // AGC: Use context so newly created revenue is instantly cached and visible in FinancesPage
  const { addRevenue } = useFinancial()
  const [formData, setFormData] = useState<RevenueFormData>({
    title: '',
    value: '',
    category: 'grain_sale',
    notes: '',
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // AGC: Validate form
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.title.trim()) {
      newErrors.title = t('financial.revenueTitle') + ' ' + t('common.required')
    }

    if (!formData.value.trim()) {
      newErrors.value = t('financial.revenueValue') + ' ' + t('common.required')
    } else {
      const numValue = parseFloat(formData.value.replace(',', '.'))
      if (isNaN(numValue) || numValue <= 0) {
        newErrors.value = 'Valor deve ser um número maior que zero'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // AGC: Handle create revenue
  const handleCreate = async () => {
    if (!validate()) return

    try {
      setIsSubmitting(true)

      // AGC: Convert value to number (handle comma as decimal separator)
      const value = parseFloat(formData.value.replace(',', '.'))

      // AGC: Always use current date/time for new revenues
      const currentDate = new Date().toISOString()

      // AGC: Capitalize first letter of title
      const capitalizeFirstLetter = (str: string): string => {
        if (!str) return str
        return str.charAt(0).toUpperCase() + str.slice(1)
      }
      const capitalizedTitle = capitalizeFirstLetter(formData.title.trim())

      // AGC: Create revenue via context — updates localStorage cache immediately
      // AGC: so when user returns to FinancesPage it shows the new item without a Firebase round-trip
      await addRevenue({
        title: capitalizedTitle,
        value: value,
        category: formData.category,
        date: currentDate,
        notes: formData.notes.trim() || undefined,
      })

      toast.success(t('financial.revenueCreated'), {
        icon: '✅',
        duration: 3000,
      })

      // AGC: Navigate back to finances page
      onNavigate('finances')
    } catch (error) {
      console.error('AGC: Error creating revenue:', error)
      toast.error(t('financial.errorCreatingRevenue'), {
        icon: '❌',
        duration: 3000,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  // AGC: Format value input (handle comma as decimal separator)
  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value
    // AGC: Remove any non-numeric characters except comma and dot
    value = value.replace(/[^\d,.]/g, '')
    setFormData({ ...formData, value })
  }

  return (
    <div className="min-h-screen bg-[#F4F5F7] dark:bg-slate-900 pb-24">
      {/* AGC: Top Bar */}
      <TopBar
        title={t('financial.newRevenue')}
        onBack={onBack}
      />

      {/* AGC: Form */}
      <div className="px-4 py-4 space-y-4">
        {/* AGC: Title Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('financial.revenueTitle')} *
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder={t('financial.revenueTitlePlaceholder')}
            className={`input ${errors.title ? 'input-error' : ''}`}
          />
          {errors.title && (
            <p className="text-red-500 text-sm mt-1">{errors.title}</p>
          )}
        </div>

        {/* AGC: Value Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('financial.revenueValue')} *
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">R$</span>
            <input
              type="text"
              value={formData.value}
              onChange={handleValueChange}
              placeholder={t('financial.valuePlaceholder')}
              className={`input pl-10 ${errors.value ? 'input-error' : ''}`}
              inputMode="decimal"
            />
          </div>
          {errors.value && (
            <p className="text-red-500 text-sm mt-1">{errors.value}</p>
          )}
        </div>

        {/* AGC: Category Dropdown */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('financial.category')} *
          </label>
          <select
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value as RevenueCategory })}
            className="input"
          >
            {REVENUE_CATEGORIES.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {t(`financial.revenueCategories.${cat.labelKey}`)}
              </option>
            ))}
          </select>
        </div>

        {/* AGC: Date Info (read-only, always current) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('financial.revenueDate')}
          </label>
          <GlassCard className="bg-gray-50 dark:bg-slate-800">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {new Date().toLocaleDateString('pt-BR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">Data/Hora atual</span>
            </div>
          </GlassCard>
        </div>

        {/* AGC: Notes (optional) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('financial.revenueNotes')}
          </label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder={t('financial.revenueNotesPlaceholder')}
            rows={3}
            className="input resize-none"
          />
        </div>
      </div>

      {/* AGC: Footer with button - matching dashboard safe area pattern */}
      <div 
        className="fixed left-0 right-0 p-4 bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700"
        style={{ bottom: 'max(env(safe-area-inset-bottom), 0px)' }}
      >
        <Button
          fullWidth
          onClick={handleCreate}
          disabled={isSubmitting}
        >
          {isSubmitting ? t('financial.saving') : t('financial.saveRevenue')}
        </Button>
      </div>
    </div>
  )
}

export default NewRevenue
